import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import ServiceDetailPriceSection from '../components/ServiceDetailPriceSection';
import { ServiceIcon, Icon } from '../components/icons';
import { VISIBLE_SERVICE_IDS, MOTORCYCLE_VISIBLE_SERVICE_IDS, HIDDEN_SERVICE_IDS } from '../data/services';
import { SERVICE_CONTENT } from '../data/serviceContent';
import { SITE_CONFIG } from '../data/siteConfig';
import { AWS_CONFIG } from '../config/aws';

// 서비스별 상세 페이지 — /services/:serviceId
// SEO 강화 목적: 키워드별 직접 매칭 + Service/Breadcrumb JSON-LD + 페이지별 차별 메타.

export default function ServiceDetailPage() {
  const { serviceId } = useParams();
  const content = SERVICE_CONTENT[serviceId];
  const [serviceData, setServiceData] = useState(null);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    fetch(`${AWS_CONFIG.apiUrl}/services`)
      .then(r => r.json())
      .then(data => {
        const found = (data.services || []).find(s => (s.serviceId || s.id) === serviceId);
        if (found) setServiceData({ ...found, id: found.serviceId || found.id });
      })
      .catch(() => {});
    fetch(`${AWS_CONFIG.apiUrl}/faqs?serviceId=${encodeURIComponent(serviceId)}`)
      .then(r => r.json())
      .then(data => setFaqs(data.faqs || []))
      .catch(() => {});
  }, [serviceId]);

  // 콘텐츠 없거나 비공개 서비스 → 서비스 인덱스로 리다이렉트
  if (!content || HIDDEN_SERVICE_IDS.includes(serviceId)) {
    return <Navigate to="/services" replace />;
  }

  // 자동차/오토바이 가격표 노출 여부 — 새 가격표 구조 기준.
  const showAutoTable = VISIBLE_SERVICE_IDS.includes(serviceId);
  const showMotorcycleTable = MOTORCYCLE_VISIBLE_SERVICE_IDS.includes(serviceId);

  // 자동차 중형 기준 가격 (페이지 상단 hero 가격 표시)
  const midPrice = serviceData?.prices?.['mid-sedan'];

  // Schema.org Service JSON-LD
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: content.h1,
    description: content.description,
    provider: {
      '@type': 'AutoWash',
      name: SITE_CONFIG.name,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '곡반정로 93',
        addressLocality: '수원시 권선구',
        addressRegion: '경기도',
        addressCountry: 'KR',
      },
      telephone: SITE_CONFIG.phone,
      url: 'https://koa-asia.com',
    },
    areaServed: [
      { '@type': 'City', name: '수원시' },
      { '@type': 'AdministrativeArea', name: '권선구' },
    ],
    ...(midPrice ? {
      offers: {
        '@type': 'Offer',
        price: midPrice,
        priceCurrency: 'KRW',
        availability: 'https://schema.org/InStock',
      },
    } : {}),
  };

  // Schema.org BreadcrumbList JSON-LD
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '홈', item: 'https://koa-asia.com/' },
      { '@type': 'ListItem', position: 2, name: '서비스 & 가격', item: 'https://koa-asia.com/services' },
      { '@type': 'ListItem', position: 3, name: content.h1, item: `https://koa-asia.com/services/${serviceId}` },
    ],
  };

  // Schema.org FAQPage JSON-LD — emitted only when FAQs exist in DB.
  const faqSchema = faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  } : null;

  return (
    <div>
      <title>{content.title}</title>
      <meta name="description" content={content.description} />
      <meta name="keywords" content={content.keywords.join(', ')} />
      <link rel="canonical" href={`https://koa-asia.com/services/${serviceId}`} />
      <meta property="og:title" content={content.title} />
      <meta property="og:description" content={content.description} />
      <meta property="og:url" content={`https://koa-asia.com/services/${serviceId}`} />
      <meta property="og:type" content="article" />

      <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      {/* Breadcrumb 화면 표시 */}
      <div className="container" style={{ paddingTop: 24, fontSize: '0.85rem' }}>
        <Link to="/" style={{ color: 'var(--text-muted)' }}>홈</Link>
        <span style={{ color: 'var(--text-dim)', margin: '0 8px' }}>›</span>
        <Link to="/services" style={{ color: 'var(--text-muted)' }}>서비스 & 가격</Link>
        <span style={{ color: 'var(--text-dim)', margin: '0 8px' }}>›</span>
        <span style={{ color: 'var(--text)' }}>{content.h1}</span>
      </div>

      {/* Service hero — 큰 시각 박스 + 아이콘 */}
      <div className="service-detail-hero">
        <div className="container" style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div className="service-detail-hero-icon">
            <ServiceIcon
              serviceId={serviceId}
              fallbackIcon={serviceData?.icon}
              size={112}
              strokeWidth={1.2}
            />
          </div>
          <div style={{ flex: '1 1 280px' }}>
          <h1 style={{ margin: 0 }}>{content.h1}</h1>
          <p style={{ fontSize: '1.05rem', color: 'var(--text-muted)' }}>
            {content.subtitle}
          </p>
          {midPrice > 0 && (
            <div style={{ marginTop: 20 }}>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>중형 기준</span>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary)' }}>
                ₩{midPrice.toLocaleString()}
                <span style={{ fontSize: '0.9rem', color: 'var(--text-dim)', marginLeft: 4 }}>~</span>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Intro + benefits */}
      <section className="section">
        <div className="container" style={{ maxWidth: 880 }}>
          <p style={{ fontSize: '1.05rem', lineHeight: 1.8, marginBottom: 32 }}>
            {content.intro}
          </p>

          <div style={{
            background: 'var(--surface)', padding: 24, borderRadius: 'var(--radius)',
            border: '1px solid var(--border)', marginBottom: 24,
          }}>
            <h2 style={{ fontSize: '1.1rem', marginBottom: 16, marginTop: 0 }}>
              <Icon name="CircleCheck" size={20} style={{ verticalAlign: 'middle', marginRight: 8, color: 'var(--primary)' }} />
              이 서비스의 강점
            </h2>
            <ul style={{ paddingLeft: 20, margin: 0, lineHeight: 1.8 }}>
              {content.benefits.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16, marginBottom: 32,
          }}>
            <div style={{ background: 'var(--surface)', padding: 20, borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <strong style={{ display: 'block', marginBottom: 8 }}>
                <Icon name="Users" size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                이런 분께 추천
              </strong>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.6 }}>
                {content.bestFor}
              </p>
            </div>
            <div style={{ background: 'var(--surface)', padding: 20, borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
              <strong style={{ display: 'block', marginBottom: 8 }}>
                <Icon name="Clock" size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                예상 소요 시간
              </strong>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.6 }}>
                {content.duration}
              </p>
            </div>
          </div>

          {/* Process */}
          <h2 style={{ fontSize: '1.3rem', marginTop: 40, marginBottom: 16 }}>작업 공정</h2>
          <ol style={{ paddingLeft: 20, lineHeight: 1.9, color: 'var(--text-muted)' }}>
            {content.process.map((p, i) => <li key={i} style={{ marginBottom: 4 }}>{p}</li>)}
          </ol>
        </div>
      </section>

      {/* Pricing */}
      <ServiceDetailPriceSection
        service={serviceData}
        showAutoTable={showAutoTable}
        showMotorcycleTable={showMotorcycleTable}
      />

      {/* FAQ — DB-managed via /admin/faq, fallback to empty when none */}
      {faqs.length > 0 && (
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container" style={{ maxWidth: 880 }}>
            <h2 style={{ fontSize: '1.3rem', marginBottom: 16 }}>자주 묻는 질문</h2>
            {faqs.map(f => (
              <details key={f.faqId} style={{
                background: 'var(--surface)', padding: '16px 20px',
                borderRadius: 'var(--radius)', border: '1px solid var(--border)',
                marginBottom: 12,
              }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  Q. {f.question}
                </summary>
                <p style={{ marginTop: 12, marginBottom: 0, color: 'var(--text-muted)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {f.answer}
                </p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container" style={{ maxWidth: 880, textAlign: 'center' }}>
          <div style={{
            background: 'var(--surface-elev)', padding: '32px 24px',
            borderRadius: 'var(--radius)', border: '1px solid var(--border)',
          }}>
            <h2 style={{ fontSize: '1.3rem', marginTop: 0, marginBottom: 12 }}>
              전화 한 통이면 일정 확인 바로 가능합니다
            </h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
              KOA DETAILING · 경기도 수원시 곡반정로 93 · 화-토 10:00~19:00
            </p>
            <a href={`tel:${SITE_CONFIG.phone}`} className="btn" style={{ padding: '12px 24px', fontSize: '0.95rem' }}>
              <Icon name="Phone" size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {SITE_CONFIG.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Related services */}
      {content.relatedServices && content.relatedServices.length > 0 && (
        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container" style={{ maxWidth: 880 }}>
            <h2 style={{ fontSize: '1.2rem', marginBottom: 16 }}>다른 서비스 둘러보기</h2>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 12,
            }}>
              {content.relatedServices.map(rid => {
                const r = SERVICE_CONTENT[rid];
                if (!r) return null;
                return (
                  <Link key={rid} to={`/services/${rid}`} className="card" style={{
                    padding: 16, textDecoration: 'none', color: 'var(--text)',
                    display: 'flex', alignItems: 'center', gap: 12,
                  }}>
                    <ServiceIcon serviceId={rid} size={28} style={{ color: 'var(--primary)' }} />
                    <div>
                      <strong style={{ display: 'block', fontSize: '0.95rem' }}>{r.h1}</strong>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{r.subtitle}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
